import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate, useSearchParams } from '@remix-run/react'
import {
  AdvancedMarker,
  APIProvider,
  ControlPosition,
  Map as GoogleMap,
  InfoWindow,
  MapCameraChangedEvent,
  RenderingType,
  useAdvancedMarkerRef,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps'
// eslint-disable-next-line @typescript-eslint/naming-convention
import _debounce from 'lodash/debounce'
import { twMerge } from 'tailwind-merge'

import { IconMapSearchPinClicked, IconMapSearchPin } from '~/components'
import {
  isSearchResultOrderType,
  OfficeInfoCard,
  SEARCH_RESULT_ORDERS,
  SearchResultOrder,
} from '~/features/common'
import { DrawerSearchResult } from '~/features/map-page/components/drawer-search-result'
import { Facility, Office, Type } from '~/models/types'

import {
  drawerContent,
  drawerPosition,
  useMapContext,
  useMapDispatchContext,
} from '../../context'
import { CloseMapButton } from '../close-map-button'
import { SearchLimitAlert } from '../search-limit-alert'
import { SearchThisAreaButton } from '../search-this-are-button'
import { UserLocationButton } from '../user-location-button'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''
const GOOGLE_MAP_ID = import.meta.env.VITE_GOOGLE_MAP_ID ?? ''

type MapMarkerProps = {
  office: Office
}

const MapMarkerComponent = ({ office }: MapMarkerProps) => {
  const { clickedPin, currentDrawerPosition } = useMapContext()
  const {
    openInfoWindow,
    closeInfoWindow,
    setDrawerContent,
    closeDrawer,
    openDrawer,
  } = useMapDispatchContext()
  const [markerRef, marker] = useAdvancedMarkerRef()

  const handleClose = () => {
    closeInfoWindow()
  }

  const isDrawerPositionBottom = currentDrawerPosition === drawerPosition.bottom
  const isPinClicked = clickedPin?.id === office.id
  const isOpenInfoWindow = isPinClicked && isDrawerPositionBottom

  const handleClick = () => {
    if (isPinClicked) {
      closeInfoWindow()
      setDrawerContent(drawerContent.list)
    } else {
      openInfoWindow(office)
      setDrawerContent(drawerContent.detail)
      // PC時はドロワーを開き、SP時は閉じる
      if (isDrawerPositionBottom) closeDrawer()
      else openDrawer()
    }
  }

  return (
    <>
      <AdvancedMarker
        className="relative"
        onClick={handleClick}
        position={{ lat: office.latitude, lng: office.longitude }}
        ref={markerRef}
        zIndex={isPinClicked ? 2 : 1}
      >
        {isPinClicked ? (
          <IconMapSearchPinClicked className="h-20 w-auto" />
        ) : (
          <IconMapSearchPin className="h-9 w-auto" />
        )}
      </AdvancedMarker>
      {isOpenInfoWindow && (
        <InfoWindow anchor={marker} minWidth={350} onClose={handleClose}>
          <OfficeInfoCard className="w-90 rounded bg-white" office={office} />
        </InfoWindow>
      )}
    </>
  )
}
const MapMarker = memo(MapMarkerComponent)

const useMapCenterLocation = (initialMapCenterLocation: MapCenterLocation) => {
  const { currentDrawerPosition } = useMapContext()
  const { closeDrawer } = useMapDispatchContext()

  const [mapCenterLocation, setMapCenterLocation] = useState<MapCenterLocation>(
    {
      lat: initialMapCenterLocation.lat,
      lng: initialMapCenterLocation.lng,
      radius: initialMapCenterLocation.radius || null,
    },
  )

  const changeMapCenterLocationState = useCallback(
    (newMapCenterLocation: Partial<MapCenterLocation>) => {
      setMapCenterLocation((prev) => {
        return {
          lat: newMapCenterLocation.lat ? newMapCenterLocation.lat : prev.lat,
          lng: newMapCenterLocation.lng ? newMapCenterLocation.lng : prev.lng,
          radius: newMapCenterLocation.radius
            ? newMapCenterLocation.radius
            : prev.radius,
        }
      })
    },
    [setMapCenterLocation],
  )

  const centerOptions = useMemo(() => {
    return {
      lat: mapCenterLocation?.lat ?? initialMapCenterLocation.lat,
      lng: mapCenterLocation?.lng ?? initialMapCenterLocation.lng,
    }
  }, [
    initialMapCenterLocation.lat,
    initialMapCenterLocation.lng,
    mapCenterLocation?.lat,
    mapCenterLocation?.lng,
  ])

  const geometry = useMapsLibrary('geometry')

  // NOTE: 中心点が変更されるたびに実行されると処理が重くなるため、debounceを使用して1秒に1回の実行に抑える
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleCenterChanged = useCallback(
    _debounce((newCenter, bounds) => {
      if (geometry && bounds) {
        // NOTE: 北東端の緯度経度を取得。これを現在表示中の地図の端までの距離とする
        const northEast = bounds.getNorthEast()
        // NOTE: 中心点から北東端までの距離を計算
        const radius = geometry.spherical.computeDistanceBetween(
          {
            lat: newCenter.lat,
            lng: newCenter.lng,
          },
          northEast,
        )

        changeMapCenterLocationState({ radius: Number(radius / 1000) })
      }
    }, 500),
    [geometry],
  )

  const handleCenterChanged = useCallback(
    (event: MapCameraChangedEvent) => {
      const newCenter = {
        lat: event.detail.center.lat,
        lng: event.detail.center.lng,
      }
      changeMapCenterLocationState(newCenter)

      const bounds = event.map.getBounds()
      debouncedHandleCenterChanged(newCenter, bounds)

      // SP時にマップを動かしたらドロワーを閉じる
      if (currentDrawerPosition === drawerPosition.bottom) closeDrawer()
    },
    [
      debouncedHandleCenterChanged,
      changeMapCenterLocationState,
      currentDrawerPosition,
      closeDrawer,
    ],
  )

  const [, setSearchParams] = useSearchParams()
  const changeMapCenterLocationParams = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('centerLatitude', mapCenterLocation.lat.toString())
      prev.set('centerLongitude', mapCenterLocation.lng.toString())

      if (mapCenterLocation.radius) {
        prev.set(
          'centerRadius',
          mapCenterLocation.radius ? mapCenterLocation.radius.toString() : '',
        )
      }

      return prev
    })
  }, [mapCenterLocation, setSearchParams])

  return {
    mapCenterLocation,
    changeMapCenterLocationState,
    changeMapCenterLocationParams,
    centerOptions,
    handleCenterChanged,
  }
}

// NOTE: 検索結果の上限を50件に設定
const OFFICE_COUNT_LIMIT = 50

const useCloseMapButton = () => {
  const navigate = useNavigate()
  const handleCloseMap = useCallback(() => {
    navigate(-1)
  }, [navigate])

  return { handleCloseMap }
}

const useAlertMessage = (totalResultCount: number) => {
  const [isAlertMessageVisible, setIsAlertMessageVisible] = useState<boolean>(
    () => totalResultCount > OFFICE_COUNT_LIMIT,
  )

  const handleCloseAlertMessage = useCallback(() => {
    setIsAlertMessageVisible(false)
  }, [setIsAlertMessageVisible])

  return { isAlertMessageVisible, handleCloseAlertMessage }
}

const SORT_ORDER_PARAM_NAME = 'sortOrder'
const useSortOrder = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const sortOrderParamString = searchParams.get(SORT_ORDER_PARAM_NAME)
  const sortOrderParam = isSearchResultOrderType(sortOrderParamString)
    ? sortOrderParamString
    : SEARCH_RESULT_ORDERS[0].value

  const changeSortOrder = useCallback(
    (sortOrder: SearchResultOrder) => {
      setSearchParams((prev) => {
        prev.set(SORT_ORDER_PARAM_NAME, sortOrder)
        return prev
      })
    },
    [setSearchParams],
  )

  return { sortOrderParam, changeSortOrder }
}

type MapCenterLocation = {
  lat: number
  lng: number
  radius: number | null
}

type MapProps = {
  offices: Office[]
  initialMapCenterLocation: MapCenterLocation
  totalResultCount: number
  officeTypes: Type[]
  facilities: Pick<Facility, 'id' | 'name' | 'order'>[]
  initialFormValues: React.ComponentProps<
    typeof DrawerSearchResult
  >['initialFormValues']
}

type MapContentProps = MapProps

const MapContent = ({
  offices,
  initialMapCenterLocation,
  totalResultCount,
  officeTypes,
  facilities,
  initialFormValues,
}: MapContentProps) => {
  const {
    mapCenterLocation,
    changeMapCenterLocationState,
    centerOptions,
    handleCenterChanged,
    changeMapCenterLocationParams,
  } = useMapCenterLocation(initialMapCenterLocation)

  const { isAlertMessageVisible, handleCloseAlertMessage } =
    useAlertMessage(totalResultCount)
  const { handleCloseMap } = useCloseMapButton()
  const { isOpenDrawer } = useMapContext()

  const handleGetUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          changeMapCenterLocationState({ lat: latitude, lng: longitude })
        },
        (error) => {
          console.error('Error getting user location:', error)
        },
      )
    } else {
      console.error('Geolocation is not supported by this browser.')
    }
  }, [changeMapCenterLocationState])

  const currentUserLocationRef = useRef<HTMLButtonElement>(null)
  const map = useMap()
  useEffect(() => {
    if (map && currentUserLocationRef.current) {
      map.controls[ControlPosition.RIGHT_CENTER].push(
        currentUserLocationRef.current,
      )
    }
  }, [map])

  const { sortOrderParam, changeSortOrder } = useSortOrder()
  const { currentDrawerPosition } = useMapContext()

  return (
    <div className="relative h-screen w-full">
      <GoogleMap
        center={centerOptions}
        defaultZoom={14}
        fullscreenControl={false}
        mapId={GOOGLE_MAP_ID}
        mapTypeControl={false}
        onCenterChanged={handleCenterChanged}
        onZoomChanged={handleCenterChanged}
        renderingType={RenderingType.RASTER}
        streetViewControl={false}
        zoomControlOptions={{
          position: ControlPosition.RIGHT_CENTER,
        }}
      >
        <div className="absolute right-0 top-0 z-10 flex w-full flex-col">
          {/* NOTE: スマホサイズの際は上に配置したいため */}
          {isAlertMessageVisible && (
            <SearchLimitAlert
              className="md:hidden"
              onClick={handleCloseAlertMessage}
            />
          )}
          {/* NOTE: ドロワー開いている際に、ドロワーの幅の分だけボタンの位置を調整 */}
          <SearchThisAreaButton
            className={twMerge(
              'absolute left-3 md:left-5 md:top-4',
              isAlertMessageVisible ? 'top-[86px]' : undefined,
              isOpenDrawer ? 'md:left-[calc(25%+32px)]' : '',
            )}
            onClick={changeMapCenterLocationParams}
          />
          <CloseMapButton
            className={twMerge(
              'absolute right-3 md:right-5 md:top-4',
              isAlertMessageVisible ? 'top-[86px]' : undefined,
            )}
            onClose={handleCloseMap}
          />
        </div>
        {/* NOTE: タブレットサイズ以降は下に配置したいため */}
        <div className="absolute bottom-4 right-5 z-10 hidden w-[465px] flex-col md:flex">
          {isAlertMessageVisible && (
            <SearchLimitAlert onClick={handleCloseAlertMessage} />
          )}
        </div>
        {offices.map((office) => (
          <MapMarker key={office.id} office={office} />
        ))}
        <UserLocationButton
          className="z-1 mx-2.5 mb-[-100px] h-10 w-10 rounded-lg bg-white"
          onClick={handleGetUserLocation}
          ref={currentUserLocationRef}
        />
        <DrawerSearchResult
          changeSortOrder={changeSortOrder}
          className="z-20" // NOTE: ドロワーのz-indexを他のボタンより上に持ってくるために設定
          facilities={facilities}
          initialFormValues={initialFormValues}
          mapCenterLocation={mapCenterLocation}
          officeTypes={officeTypes}
          offices={offices}
          position={currentDrawerPosition}
          searchResultCount={totalResultCount}
          sortOrder={sortOrderParam}
        />
      </GoogleMap>
    </div>
  )
}

export const Map = (props: MapProps) => {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <MapContent {...props} />
    </APIProvider>
  )
}
